import React, { useCallback } from 'react';
import { RoomAudioRenderer, useAudioPlayback } from '@livekit/components-react';
import type { Room } from 'livekit-client';
import { Button } from '../common/Button.js';
import { VIDEO_CONFIG } from '../../config/videoConfig.js';

interface VideoRoomAudioProps {
  /** The connected LiveKit room. Audio lives and dies with this object. */
  room: Room;
}

/**
 * Plays every remote audio track of a LiveKit room.
 *
 * `RoomAudioRenderer` subscribes to the Microphone, ScreenShareAudio and
 * Unknown sources, drops the local participant and renders one hidden
 * `<AudioTrack>` per remaining track. Without it the subscribed audio bytes
 * arrive and are discarded — nothing ever attaches them to an audio element.
 *
 * Its lifetime must follow the *room*, not the visibility of the video tiles:
 * the common case (listener with the camera off, alone in an area) hides the
 * overlay entirely, and that is exactly when audio matters most.
 *
 * Browsers also refuse to start playback without a user gesture, and the call
 * auto-connects shortly after entering a private area. `useAudioPlayback`
 * reports that blocked state so we can offer a one-click unblock rendered with
 * the app's own `Button`; it disappears as soon as playback is allowed.
 */
export const VideoRoomAudio: React.FC<VideoRoomAudioProps> = ({ room }) => {
  const { canPlayAudio, startAudio } = useAudioPlayback(room);

  const handleEnableAudio = useCallback(() => {
    startAudio().catch((err) => {
      console.error('🎥 Failed to start audio playback:', err);
    });
  }, [startAudio]);

  return (
    <>
      <RoomAudioRenderer />
      {!canPlayAudio && (
        <div
          className={`${VIDEO_CONFIG.position.audioUnblock} ${VIDEO_CONFIG.position.audioUnblockZIndex} pointer-events-auto`}
          data-testid="audio-playback-blocked"
        >
          <Button
            variant="primary"
            onClick={handleEnableAudio}
            aria-label="Enable audio playback"
          >
            🔊 Click to enable audio
          </Button>
        </div>
      )}
    </>
  );
};

VideoRoomAudio.displayName = 'VideoRoomAudio';
